import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { isDoctorTransferEnabled } from "../../../../utils/clinicFeatures";

const cards = [
  { key: "appointments", label: "All", icon: EventIcon, bg: "bg-amber-100 dark:bg-amber-900/40", text: "text-amber-700 dark:text-amber-300", iconBg: "bg-amber-500", ring: "ring-amber-400/60", hide: false },
  { key: "schedule", label: "Schedule", icon: ScheduleIcon, bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-700 dark:text-orange-300", iconBg: "bg-orange-500", ring: "ring-orange-400/60", hide: false },
  { key: "walkin", label: "Walk-in", icon: DirectionsWalkIcon, bg: "bg-teal-100 dark:bg-teal-900/40", text: "text-teal-700 dark:text-teal-300", iconBg: "bg-teal-500", ring: "ring-teal-400/60", hide: false },
  { key: "callin", label: "Call-in", icon: PhoneInTalkIcon, bg: "bg-cyan-100 dark:bg-cyan-900/40", text: "text-cyan-700 dark:text-cyan-300", iconBg: "bg-cyan-500", ring: "ring-cyan-400/60", hide: false },
  { key: "inqueue", label: "In queue", icon: PeopleIcon, bg: "bg-sky-100 dark:bg-sky-900/40", text: "text-sky-700 dark:text-sky-300", iconBg: "bg-sky-500", ring: "ring-sky-400/60", hide: false },
  { key: "ongoing", label: "Ongoing", icon: PlayCircleIcon, bg: "bg-lime-100 dark:bg-lime-900/40", text: "text-lime-700 dark:text-lime-300", iconBg: "bg-lime-500", ring: "ring-lime-400/60", hide: false },
  { key: "waiting", label: "Waiting", icon: HourglassBottomIcon, bg: "bg-violet-100 dark:bg-violet-900/40", text: "text-violet-700 dark:text-violet-300", iconBg: "bg-violet-500", ring: "ring-violet-400/60", hide: false },
  { key: "completed", label: "Complete", icon: CheckCircleIcon, bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-300", iconBg: "bg-emerald-500", ring: "ring-emerald-400/60", hide: false },
  { key: "cancelled", label: "Cancel", icon: CancelIcon, bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-700 dark:text-red-300", iconBg: "bg-red-500", ring: "ring-red-400/60", hide: false },
  { key: "assigned", label: "Assigned", icon: SwapHorizIcon, bg: "bg-fuchsia-100 dark:bg-fuchsia-900/40", text: "text-fuchsia-700 dark:text-fuchsia-300", iconBg: "bg-fuchsia-500", ring: "ring-fuchsia-400/60", hide: false, requiresDoctorTransfer: true },
  // kept but hidden
  { key: "reschudle", label: "Rescheduled", icon: EventRepeatIcon, bg: "bg-indigo-100 dark:bg-indigo-900/40", text: "text-indigo-700 dark:text-indigo-300", iconBg: "bg-indigo-500", ring: "ring-indigo-400/60", hide: true },
  { key: "noshow", label: "No Show", icon: VisibilityOffIcon, bg: "bg-slate-100 dark:bg-slate-700", text: "text-slate-700 dark:text-slate-200", iconBg: "bg-slate-500", ring: "ring-slate-400/60", hide: true },
];

/** Keep the last row full (no lonely orphan card) by matching column count to visible card count. */
const LG_GRID_COLS_CLASS: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
  7: "lg:grid-cols-7",
  8: "lg:grid-cols-8",
  9: "lg:grid-cols-9",
  10: "lg:grid-cols-10",
  11: "lg:grid-cols-11",
};

function lgGridColsClass(count: number): string {
  return LG_GRID_COLS_CLASS[count] ?? "lg:grid-cols-8";
}

/** Status-based counts:
 *  Schedule = upcoming (appointment/pending) + walkin + callin
 *  All = pending + walkin + callin + inqueue + ongoing + waiting + completed + cancelled
 *  Others = direct status counts from API
 */
function resolveCount(
  count: Record<string, number | undefined> | undefined,
  key: string
): number {
  if (key === "schedule") {
    if (count?.schedule != null) return Number(count.schedule);
    return (
      Number(count?.pending ?? 0) +
      Number(count?.walkin ?? 0) +
      Number(count?.callin ?? 0)
    );
  }
  if (key === "appointments") {
    // Prefer API total when present (includes walkin + callin)
    if (count?.appointments != null) return Number(count.appointments);
    return (
      Number(count?.pending ?? 0) +
      Number(count?.walkin ?? 0) +
      Number(count?.callin ?? 0) +
      Number(count?.inqueue ?? 0) +
      Number(count?.ongoing ?? 0) +
      Number(count?.waiting ?? 0) +
      Number(count?.completed ?? 0) +
      Number(count?.cancelled ?? 0)
    );
  }
  return Number(count?.[key] ?? 0);
}

function AppointmentCounts({
  count,
  compact = false,
  activeKey,
  onSelect,
  selectableKeys,
}: {
  count: Record<string, number | undefined>;
  compact?: boolean;
  activeKey?: string;
  onSelect?: (key: string) => void;
  selectableKeys?: string[];
}) {
  const doctorTransferEnabled = isDoctorTransferEnabled();
  const visibleCards = cards.filter(
    ({ hide, requiresDoctorTransfer }) =>
      !hide && (!requiresDoctorTransfer || doctorTransferEnabled)
  );

  if (compact) {
    return (
      <div
        className={`grid grid-cols-2 sm:grid-cols-4 ${lgGridColsClass(
          visibleCards.length
        )} gap-1.5`}
      >
        {visibleCards.map(({ key, label, icon: Icon, bg, text, iconBg, ring }) => {
          const isSelectable =
            onSelect != null &&
            (selectableKeys == null || selectableKeys.includes(key));
          const isActive = activeKey === key;

          const inner = (
            <>
              <div
                className={`hidden sm:flex w-6 h-6 shrink-0 ${iconBg} rounded-md items-center justify-center shadow-sm`}
              >
                <Icon className="text-white" sx={{ fontSize: 14 }} />
              </div>
              <div className="min-w-0">
                <p className={`text-sm sm:text-base font-bold ${text} leading-none tabular-nums`}>
                  {resolveCount(count, key)}
                </p>
                <p
                  className={`text-[8px] sm:text-[9px] font-medium ${text} uppercase tracking-wide truncate`}
                >
                  {label}
                </p>
              </div>
            </>
          );

          const className = [
            bg,
            "rounded-lg px-2 py-2 flex items-center gap-1.5 min-w-0 transition-all duration-200",
            isSelectable
              ? "cursor-pointer hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
              : "",
            isActive ? `ring-2 ${ring} shadow-md scale-[1.02]` : "",
          ]
            .filter(Boolean)
            .join(" ");

          if (isSelectable) {
            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(key)}
                className={className}
                aria-pressed={isActive}
                title={`Show ${label.toLowerCase()} appointments`}
              >
                {inner}
              </button>
            );
          }

          return (
            <div key={key} className={className}>
              {inner}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-2 sm:grid-cols-4 ${lgGridColsClass(
        visibleCards.length
      )} gap-1.5 sm:gap-2 p-1.5`}
    >
      {visibleCards.map(({ key, label, icon: Icon, bg, text, iconBg }) => (
        <div key={key} className={`${bg} rounded-lg sm:rounded-xl p-1 sm:p-1.5 flex flex-row sm:flex-col items-center justify-center transition-colors`}>
          <div className="hidden sm:flex w-7 h-7 items-center justify-center sm:mb-1.5">
            <div className={`w-7 h-7 ${iconBg} rounded-lg flex items-center justify-center`}>
              <Icon className="text-white" sx={{ fontSize: 18 }} />
            </div>
          </div>
          <div className="flex flex-row sm:flex-col items-center justify-center gap-1.5 sm:gap-0">
            <h3 className={`text-sm sm:text-xl font-bold ${text} leading-tight`}>{resolveCount(count, key)}</h3>
            <span className={`text-[6px] sm:text-[8px] font-medium ${text} uppercase tracking-wide text-center leading-tight`}>{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default AppointmentCounts;

